import React, { useMemo, useRef, useState } from "react";
import { Copy, Loader2, Share2 } from "lucide-react";
import { bettorDisplayName } from "@/lib/bettorDisplayName";
import { useLocale } from "@/context/LocaleContext";

function bd(r, key) {
  return r.breakdown?.[key] ?? 0;
}

function ChartLegend({ segments, compact = false }) {
  return (
    <div
      className={`mt-3 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] sm:text-sm leading-tight text-[var(--text-dim)] ${
        compact ? "justify-center" : "justify-center sm:justify-start"
      }`}
    >
      {segments.map((s) => (
        <div key={s.id} className="inline-flex items-center gap-1.5 shrink-0">
          <span className={`inline-block w-2.5 h-2.5 rounded-sm shrink-0 ${s.color}`} aria-hidden />
          <span>{s.label}</span>
        </div>
      ))}
    </div>
  );
}

function ChartToolbar({ segment, setSegment, filterOptions, chartRef, title, t }) {
  const [copyBusy, setCopyBusy] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const busy = copyBusy || shareBusy;

  const onCopy = async () => {
    try {
      setCopyBusy(true);
      const { copyNodeImageToClipboard } = await import("@/lib/chartCapture");
      const { toast } = await import("@/hooks/use-toast");
      await copyNodeImageToClipboard(chartRef.current, { pixelRatio: 2 });
      toast({ title: t("common.copied") });
    } catch {
      const { toast } = await import("@/hooks/use-toast");
      toast({ title: t("common.copyImageFailed") });
    } finally {
      setCopyBusy(false);
    }
  };

  const onShare = async () => {
    try {
      setShareBusy(true);
      const { shareNodeImage } = await import("@/lib/chartCapture");
      const { toast } = await import("@/hooks/use-toast");
      const result = await shareNodeImage(chartRef.current, {
        title,
        filename: "grupptipset-diagram.png",
        pixelRatio: 2,
      });
      if (result === "downloaded") {
        toast({ title: t("common.shareDownloaded") });
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
      const { toast } = await import("@/hooks/use-toast");
      toast({ title: t("common.shareImageFailed") });
    } finally {
      setShareBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-center gap-2">
      <label className="flex items-center gap-2 text-xs text-[var(--text-dim)]">
        <span className="whitespace-nowrap">{t("lb.chart.filterLabel")}</span>
        <select
          value={segment}
          onChange={(e) => setSegment(e.target.value)}
          className="bg-[var(--panel)] border border-[var(--line)] rounded px-2 py-1 text-sm text-[var(--text)]"
        >
          {filterOptions.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      <button
        type="button"
        disabled={busy}
        onClick={onCopy}
        className="chart-toolbar-icon-btn"
        title={t("common.copyImage")}
        aria-label={t("common.copyImage")}
      >
        {copyBusy ? <Loader2 className="chart-toolbar-icon-btn__icon animate-spin" /> : <Copy className="chart-toolbar-icon-btn__icon" />}
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={onShare}
        className="chart-toolbar-icon-btn"
        title={t("common.shareImage")}
        aria-label={t("common.shareImage")}
      >
        {shareBusy ? <Loader2 className="chart-toolbar-icon-btn__icon animate-spin" /> : <Share2 className="chart-toolbar-icon-btn__icon" />}
      </button>
    </div>
  );
}

/**
 * Horizontal stacked bar chart: participants on Y-axis, points on X-axis.
 */
export default function LeaderboardChartBlock({ rows, segments, title, t }) {
  const { lang } = useLocale();
  const [segment, setSegment] = useState("all");
  const chartRef = useRef(null);

  const dateLabel = useMemo(() => {
    const loc = lang === "en" ? "en-GB" : "sv-SE";
    return new Date().toLocaleDateString(loc, { day: "numeric", month: "short", year: "numeric" });
  }, [lang]);

  const ordered = useMemo(() => {
    const rs = (rows || []).slice();
    rs.sort((a, b) => (Number(b.score) || 0) - (Number(a.score) || 0));
    return rs;
  }, [rows]);

  const maxTotal = useMemo(() => Math.max(0, ...ordered.map((r) => Number(r.score) || 0)), [ordered]);
  const maxSeg = useMemo(() => {
    if (segment === "all") return maxTotal;
    return Math.max(0, ...ordered.map((r) => Number(bd(r, segment)) || 0));
  }, [ordered, segment, maxTotal]);

  const segValue = (r, id) => Number(bd(r, id)) || 0;
  const totalValue = (r) => Number(r.score) || 0;

  const filterOptions = useMemo(
    () => [{ id: "all", label: t("lb.chart.filterAll") }, ...segments.map((s) => ({ id: s.id, label: s.label }))],
    [segments, t],
  );

  const renderStack = (r) => {
    const total = totalValue(r);
    const denom = maxTotal || 1;
    const scalePct = segment !== "all"
      ? ((segValue(r, segment) / (maxSeg || 1)) * 100)
      : ((total / denom) * 100);

    if (segment !== "all") {
      const s = segments.find((x) => x.id === segment);
      const v = s ? segValue(r, s.id) : 0;
      const w = scalePct;
      return (
        <div className="relative h-3 w-full">
          <div
            className="absolute inset-y-0 left-0 h-3 rounded-full overflow-hidden"
            style={{ width: `${w}%` }}
          >
            <div className={`h-full w-full ${s?.color || "bg-[var(--accent)]"}`} />
          </div>
        </div>
      );
    }

    let acc = 0;
    const active = segments.filter((s) => segValue(r, s.id) > 0);
    return (
      <div className="relative h-3 w-full">
        <div
          className="absolute inset-y-0 left-0 h-3 rounded-full overflow-hidden"
          style={{ width: `${scalePct}%` }}
        >
          {active.map((s) => {
            const v = segValue(r, s.id);
            const pct = total > 0 ? (v / total) * 100 : 0;
            const left = acc;
            acc += pct;
            return (
              <div
                key={s.id}
                className={`absolute inset-y-0 ${s.color}`}
                style={{ left: `${left}%`, width: `${pct}%` }}
                title={`${s.label}: ${v}`}
              />
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <ChartToolbar
          segment={segment}
          setSegment={setSegment}
          filterOptions={filterOptions}
          chartRef={chartRef}
          title={title}
          t={t}
        />
      </div>

      <div
        ref={chartRef}
        className="chart-capture-root rounded-xl border border-[var(--line)] bg-[var(--panel)] p-3 sm:p-4 text-[var(--text)]"
      >
        <div className="flex items-baseline justify-between gap-3 mb-3">
          <h4 className="flex-1 text-center font-display text-base sm:text-lg text-[var(--accent)] chart-capture-title leading-snug">
            {title}
          </h4>
          <span className="shrink-0 text-[11px] sm:text-xs text-[var(--text-dim)] chart-capture-muted whitespace-nowrap">{dateLabel}</span>
        </div>
        <div className="space-y-2.5">
          {ordered.map((r) => {
            const v = segment === "all" ? totalValue(r) : segValue(r, segment);
            return (
              <div key={r.user_id} className="grid grid-cols-[minmax(0,1fr)_1.75rem] gap-x-1.5 items-end">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-[var(--text)] chart-capture-name mb-1" title={r.name}>
                    {bettorDisplayName(r.name)}
                  </div>
                  {renderStack(r)}
                </div>
                <div className="shrink-0 font-mono text-xs text-[var(--text-dim)] chart-capture-muted tabular-nums text-right leading-none pb-0.5">
                  {v}
                </div>
              </div>
            );
          })}
        </div>
        {segment === "all" && <ChartLegend segments={segments} />}
      </div>
    </div>
  );
}
